import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { FileUploadField } from '../components/FileUploadField';
import { saveOnboarding, submitPublicShare } from '../utils/api';
import { ENTITY_TYPES, getEntityDocs, PERSON_LABEL } from '../data/entityOnboardingConfig';
import ConsentNotice from '../components/ConsentNotice';
import { buildConsent, CONSENT_PURPOSE } from '../utils/dpdp';

const emptyPerson = () => ({ name: '', designation: '', pan_number: '', aadhaar_number: '', pan_doc: '', aadhaar_doc: '', photo: '' });

const NonIndividualOnboarding = ({ publicToken = null, onSubmitted = null }) => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);
  const [form, setForm] = useState({
    client_type: 'non_individual',
    entity_type: 'Company',
    entity_name: '', entity_pan_number: '', date_of_incorporation: '',
    registered_address: '', city: '', state: '', pincode: '', email: '', phone: '',
    bank_name: '', account_number: '', account_type: 'Current', ifsc: '', micr: '', branch: '',
    authorised_persons: [emptyPerson(), emptyPerson()],
    documents: {},
  });

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setDoc = (key, url) => setForm((p) => ({ ...p, documents: { ...p.documents, [key]: url } }));
  const updatePerson = (i, k, v) => setForm((p) => {
    const persons = p.authorised_persons.map((x, idx) => (idx === i ? { ...x, [k]: v } : x));
    return { ...p, authorised_persons: persons };
  });
  const addPerson = () => setForm((p) => ({ ...p, authorised_persons: [...p.authorised_persons, emptyPerson()] }));
  const removePerson = (i) => setForm((p) => ({ ...p, authorised_persons: p.authorised_persons.filter((_, idx) => idx !== i) }));

  const docs = getEntityDocs(form.entity_type);
  const personLabel = PERSON_LABEL[form.entity_type] || 'Authorised Person';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.entity_name.trim()) { toast.error('Please enter the entity name'); return; }
    if (!form.entity_pan_number.trim()) { toast.error('Please enter the entity PAN'); return; }
    const validPersons = form.authorised_persons.filter((p) => p.name.trim());
    if (validPersons.length < 2) { toast.error(`Please add at least 2 ${personLabel.toLowerCase()}s with a name`); return; }
    const incomplete = validPersons.find((p) => !p.pan_doc || !p.aadhaar_doc || !p.photo);
    if (incomplete) { toast.error(`Each ${personLabel.toLowerCase()} needs PAN, Aadhaar & a photograph uploaded (check ${incomplete.name}).`); return; }
    const missing = docs.filter((d) => d.required && !form.documents[d.key]);
    if (missing.length) { toast.error(`Please upload: ${missing.map((d) => d.label).join(', ')}`); return; }
    if (!consentGiven) { toast.error('Please provide consent under the Data Protection Notice before submitting.'); return; }

    setSaving(true);
    const record = { ...form, authorised_persons: validPersons, consent: buildConsent(CONSENT_PURPOSE.entity_onboarding, { assisted: !publicToken }), created_at: new Date().toISOString() };
    try {
      if (publicToken) {
        const res = await submitPublicShare(publicToken, record);
        onSubmitted?.(res.data);
        return;
      }
      const id = await saveOnboarding(record);
      if (id) { toast.success('Non-individual onboarding saved'); navigate(`/admin/onboarding/view/${id}`); }
      else toast.error('Error saving. Please try again.');
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'mt-1';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
      <div className="max-w-5xl mx-auto" data-testid="non-individual-onboarding">
        {!publicToken && (
          <Button variant="outline" onClick={() => navigate('/admin/onboarding')} className="mb-6">
            <ArrowLeft size={20} className="mr-2" /> Back to Onboarding List
          </Button>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Building2 className="text-blue-600" /> Non-Individual Onboarding</CardTitle>
              <p className="text-sm text-slate-600">KYC details & documents for a company, trust, firm or HUF.</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Entity Type *</Label>
                  <select
                    value={form.entity_type}
                    onChange={(e) => setField('entity_type', e.target.value)}
                    className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="entity-type-select"
                  >
                    {ENTITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Entity Name *</Label>
                  <Input className={inputCls} value={form.entity_name} onChange={(e) => setField('entity_name', e.target.value)} placeholder="Registered name of the entity" data-testid="entity-name" />
                </div>
                <div>
                  <Label>Entity PAN *</Label>
                  <Input className={inputCls} value={form.entity_pan_number} onChange={(e) => setField('entity_pan_number', e.target.value.toUpperCase())} placeholder="ABCDE1234F" data-testid="entity-pan" />
                </div>
                <div>
                  <Label>Date of Incorporation / Formation</Label>
                  <Input type="date" className={inputCls} value={form.date_of_incorporation} onChange={(e) => setField('date_of_incorporation', e.target.value)} />
                </div>
                <div className="md:col-span-2">
                  <Label>Registered Address</Label>
                  <Input className={inputCls} value={form.registered_address} onChange={(e) => setField('registered_address', e.target.value)} placeholder="Registered office address" />
                </div>
                <div><Label>City</Label><Input className={inputCls} value={form.city} onChange={(e) => setField('city', e.target.value)} /></div>
                <div><Label>State</Label><Input className={inputCls} value={form.state} onChange={(e) => setField('state', e.target.value)} /></div>
                <div><Label>Pincode</Label><Input className={inputCls} value={form.pincode} onChange={(e) => setField('pincode', e.target.value)} /></div>
                <div><Label>Email</Label><Input type="email" className={inputCls} value={form.email} onChange={(e) => setField('email', e.target.value)} /></div>
                <div><Label>Phone</Label><Input className={inputCls} value={form.phone} onChange={(e) => setField('phone', e.target.value)} /></div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Entity Bank Details</CardTitle></CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4">
              <div><Label>Bank Name</Label><Input className={inputCls} value={form.bank_name} onChange={(e) => setField('bank_name', e.target.value)} /></div>
              <div><Label>Account Number</Label><Input className={inputCls} value={form.account_number} onChange={(e) => setField('account_number', e.target.value)} /></div>
              <div>
                <Label>Account Type</Label>
                <select value={form.account_type} onChange={(e) => setField('account_type', e.target.value)} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg">
                  <option>Current</option><option>Savings</option><option>Cash Credit / OD</option>
                </select>
              </div>
              <div><Label>IFSC</Label><Input className={inputCls} value={form.ifsc} onChange={(e) => setField('ifsc', e.target.value.toUpperCase())} /></div>
              <div><Label>MICR</Label><Input className={inputCls} value={form.micr} onChange={(e) => setField('micr', e.target.value)} /></div>
              <div><Label>Branch</Label><Input className={inputCls} value={form.branch} onChange={(e) => setField('branch', e.target.value)} /></div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>{personLabel}s</CardTitle>
                <p className="text-sm text-slate-500 mt-1">Add at least 2. Each needs Aadhaar, PAN & a photograph.</p>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={addPerson} className="flex items-center gap-1" data-testid="add-person-btn">
                <Plus size={14} /> Add {personLabel}
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              {form.authorised_persons.map((person, i) => (
                <div key={i} className="border-2 border-slate-200 rounded-lg p-4 space-y-3" data-testid={`person-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-600">{personLabel} {i + 1}</span>
                    {form.authorised_persons.length > 2 && (
                      <Button type="button" variant="ghost" size="sm" className="text-red-600" onClick={() => removePerson(i)}><Trash2 size={14} /></Button>
                    )}
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div><Label>Name</Label><Input className={inputCls} value={person.name} onChange={(e) => updatePerson(i, 'name', e.target.value)} data-testid={`person-${i}-name`} /></div>
                    <div><Label>Designation / Role</Label><Input className={inputCls} value={person.designation} onChange={(e) => updatePerson(i, 'designation', e.target.value)} /></div>
                    <div><Label>PAN</Label><Input className={inputCls} value={person.pan_number} onChange={(e) => updatePerson(i, 'pan_number', e.target.value.toUpperCase())} /></div>
                    <div><Label>Aadhaar Number</Label><Input className={inputCls} value={person.aadhaar_number} onChange={(e) => updatePerson(i, 'aadhaar_number', e.target.value)} /></div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-3">
                    <FileUploadField label="PAN Document" value={person.pan_doc} onChange={(url) => updatePerson(i, 'pan_doc', url)} publicToken={publicToken} />
                    <FileUploadField label="Aadhaar Document" value={person.aadhaar_doc} onChange={(url) => updatePerson(i, 'aadhaar_doc', url)} publicToken={publicToken} />
                    <FileUploadField label="Photograph" value={person.photo} onChange={(url) => updatePerson(i, 'photo', url)} publicToken={publicToken} accept="image/*" />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Required Documents — {form.entity_type}</CardTitle>
              <p className="text-sm text-slate-500 mt-1">Documents update automatically based on the selected entity type.</p>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4">
              {docs.map((d) => (
                <FileUploadField
                  key={d.key}
                  label={d.label}
                  hint={d.hint}
                  required={d.required}
                  value={form.documents[d.key] || ''}
                  onChange={(url) => setDoc(d.key, url)}
                  publicToken={publicToken}
                />
              ))}
            </CardContent>
          </Card>

          <div className="mb-2">
            <ConsentNotice
              purpose={CONSENT_PURPOSE.entity_onboarding}
              checked={consentGiven}
              onChange={setConsentGiven}
              assisted={!publicToken}
              items={['Entity name, PAN, incorporation & registered address', 'Entity bank details', 'Authorised signatory details (name, PAN, Aadhaar, photo)', 'Entity KYC documents you upload']}
            />
          </div>

          <div className="flex gap-4 pb-10">
            <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-entity-onboarding">
              {saving ? 'Saving…' : (publicToken ? 'Submit Onboarding' : 'Complete Onboarding')}
            </Button>
            {!publicToken && <Button type="button" variant="outline" onClick={() => navigate('/admin/onboarding')}>Cancel</Button>}
          </div>
        </form>
      </div>
    </div>
  );
};

export default NonIndividualOnboarding;
